import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import Image from '../../../components/Image';
import { SidebarCard } from '../components/SidebarCard';
import { useAdModal } from '../../../ads/useAdModal';

/**
 * One ad, rendered into the sidebar's card shell.
 *
 * ─── Asset spec, for the `image` field ──────────────────────────────────────
 * Measured off the live card, not derived from the CSS:
 *
 *   drawing area   219px wide (full-bleed, as below) — 193px if inset
 *   target height  120px  (~16:9). Card lands ~320px tall.
 *   hard ceiling   160px. Past that the card scrolls on a 1366x768 laptop,
 *                  because the slot's height budget keeps the profile row on
 *                  screen rather than letting a tall card push it off.
 *   background     transparent, sitting on white (#FFFFFF)
 *   format         SVG preferred (outline any text, or leave text out);
 *                  otherwise transparent PNG at @2x (438x240) and @3x (657x360)
 *   brand green    emerald-600 #00875C
 *
 * At this width a chart cannot carry axis labels, a legend, or more than ~3-4
 * categories at readable type. Keep words OUT of the asset and in `headline` /
 * `body`, where they scale, translate and reach a screen reader.
 *
 * The image bleeds to the card's left and right edges (-mx-3 cancels SidebarCard's
 * px-3, the same trick BtsTipCard uses for its hover tint) but not to the top: it
 * sits below a little of the card's own padding, and closer to the headline than
 * to the top edge. Artwork tends to carry its own internal whitespace, so the
 * measured gaps here read larger than their pixel values.
 */
function AdImage({ src, alt }) {
  if (!src) return null;
  return (
    <div className='-mx-3 -mt-3 overflow-hidden'>
      <Image src={src} alt={alt} className='block w-full' />
    </div>
  );
}

export function SidebarAdCard({ ad, onDismiss, onClick, onTrack }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { Modal, isModalOpen, openModal, closeModal } = useAdModal({
    ad,
    onClick,
    componentName: 'SidebarAdCard',
  });
  const [copied, setCopied] = useState(false);

  // With `copyText`, copying is the CTA (for web Gmail users, whose mail link opens nothing) and
  // the `href` drops to the link row as `emailLabel`.
  const copyMessage = () => {
    navigator.clipboard?.writeText(ad.copyText).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onClick?.();
  };
  const linkClass =
    'whitespace-nowrap underline hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded';

  // A descriptor's `prompt` drops its text into the workshop input instead of
  // going anywhere. Router navigation rather than an href: an <a> to /?prompt=
  // reloads the page and throws away every other query param — which, among
  // other things, ends the preview the reviewer is looking at.

  const fillPrompt = () => {
    onClick?.();
    const next = new URLSearchParams(searchParams);
    next.set('prompt', ad.prompt);
    navigate({ pathname: '/', search: `?${next}` });
  };

  // Layout mirrors the slot's other cards: a lead element, then headline, copy,
  // and an emerald CTA row.
  const body = (
    <>
      {/* An ad has one lead element or none. Not a bare emoji paragraph: a
          descriptor still waiting on its artwork has neither, and an empty <p>
          would still reserve its 32px line above the headline. */}
      {ad.image ? <AdImage src={ad.image} alt={ad.imageAlt ?? ''} /> : null}
      {!ad.image && ad.emoji ? <p className='text-2xl mb-1'>{ad.emoji}</p> : null}
      {/* Tighter than the welcome card's mb-5: an ad's headline and body are one
          thought, so they read as a pair rather than two stacked blocks. */}
      <p className='text-base font-medium text-gray-900 mb-2'>{ad.headline}</p>
      <p className='text-sm text-gray-500 leading-relaxed font-light'>{ad.body}</p>
    </>
  );

  const ctaRow = (
    <span className='mt-6 flex items-center gap-x-0.5 text-sm font-medium text-emerald-700'>
      {copied ? 'Copied!' : ad.ctaLabel}
      <ChevronRightIcon className='h-4 w-4' />
    </span>
  );

  return (
    <SidebarCard onDismiss={onDismiss}>
      {body}
      {ad.copyText || ad.prompt ? (
        <button
          type='button'
          onClick={ad.copyText ? copyMessage : fillPrompt}
          className='block text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded'>
          {ctaRow}
        </button>
      ) : ad.href ? (
        <a
          href={ad.href}
          // An in-app href stays in this tab: the sidebar is beside the workshop,
          // so "start a math resource" should land there, not in a new window.
          {...(ad.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          onClick={onClick}
          className='block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded'>
          {ctaRow}
        </a>
      ) : Modal ? (
        <button
          type='button'
          onClick={openModal}
          className='block text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded'>
          {ctaRow}
        </button>
      ) : null}
      {ad.copyText || ad.learnMoreHref ? (
        <span className='mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500'>
          {ad.copyText && ad.href ? (
            <a href={ad.href} onClick={onClick} className={linkClass}>
              {ad.emailLabel}
            </a>
          ) : null}
          {ad.learnMoreHref ? (
            <a
              href={ad.learnMoreHref}
              target='_blank'
              rel='noopener noreferrer'
              className={linkClass}>
              Learn how
            </a>
          ) : null}
        </span>
      ) : null}
      {Modal && (
        <Modal isOpen={isModalOpen} onClose={closeModal} onTrack={onTrack} {...ad.modal.props} />
      )}
    </SidebarCard>
  );
}

export default SidebarAdCard;
